import { useState, useEffect } from 'react';
import HtmlEditor from './HtmlEditor';

/**
 * Email Template Customization Modal
 * 
 * Allows admins to customize email templates for different notification types
 * 
 * @author SureCost Development Team
 * @version 1.0.0
 */
const EmailTemplateModal = ({ isOpen, onClose, notificationType, onSave }) => {
    const [template, setTemplate] = useState({
        subject: '',
        htmlContent: '',
        textContent: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('preview');

    useEffect(() => {
        if (isOpen && notificationType) {
            // Reset template state when modal opens
            setTemplate({
                subject: '',
                htmlContent: '',
                textContent: ''
            });
            loadTemplate();
        }
    }, [isOpen, notificationType]);

    const loadTemplate = async () => {
        try {
            setIsLoading(true);
            const response = await fetch(`/api/admin/email-templates/${notificationType}`);
            const data = await response.json();
            
            if (data.success && data.template) {
                setTemplate(data.template);
                console.log('Template loaded:', data.template.subject);
            } else {
                console.error('Failed to load template:', data);
            }
        } catch (error) {
            console.error('Template loading error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setIsLoading(true);
            const response = await fetch(`/api/admin/email-templates/${notificationType}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify(template)
            });

            const result = await response.json();
            
            if (result.success) {
                onSave();
                onClose();
                alert('Email template saved successfully!');
            } else {
                alert(`Failed to save template: ${result.message}`);
            }
        } catch (error) {
            console.error('Failed to save email template:', error);
            alert('Failed to save email template.');
        } finally {
            setIsLoading(false);
        }
    };

    const getTemplateTitle = () => {
        switch (notificationType) {
            case 'magicLink':
                return 'Magic Link Authentication Email';
            case 'welcome':
                return 'Welcome Email Template';
            default:
                return 'Email Template';
        }
    };

    const getDefaultPreview = () => {
        switch (notificationType) {
            case 'magicLink':
                return `
                    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
                        <h2 style="color: #1f2937;">SureCost - Secure Sign-in Link</h2>
                        <p>Hello,</p>
                        <p>Click the button below to securely sign in to your SureCost Vendor Dashboard:</p>
                        <div style="margin: 30px 0; text-align: center;">
                            <a href="#" style="background-color: #1f2937; color: white; padding: 14px 28px; text-decoration: none; border-radius: 6px; font-weight: 600;">
                                Sign In to SureCost
                            </a>
                        </div>
                        <p style="color: #6b7280; font-size: 14px;">This link will expire in 15 minutes for security purposes.</p>
                        <p style="color: #6b7280; font-size: 14px;">If you didn't request this sign-in link, please ignore this email.</p>
                    </div>
                `;
            case 'welcome':
                return `
                    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
                        <h2 style="color: #1f2937;">Welcome to SureCost!</h2>
                        <p>Hello and welcome to the SureCost Vendor Dashboard!</p>
                        <p>We're excited to have you on board. Here's what you can do with your new account:</p>
                        <ul style="color: #374151;">
                            <li>Access real-time pricing intelligence</li>
                            <li>View market comparisons and trends</li>
                            <li>Manage your vendor account settings</li>
                            <li>Generate detailed reports</li>
                        </ul>
                        <div style="margin: 30px 0; text-align: center;">
                            <a href="#" style="background-color: #1f2937; color: white; padding: 14px 28px; text-decoration: none; border-radius: 6px; font-weight: 600;">
                                Access Your Dashboard
                            </a>
                        </div>
                        <p style="color: #6b7280; font-size: 14px;">If you have any questions, feel free to contact our support team.</p>
                    </div>
                `;
            default:
                return '<p>Email template preview will appear here.</p>';
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <>
            <style>
                {`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}
            </style>
            <div 
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem',
                    zIndex: 9999
                }}
            >
            <div 
                style={{
                    backgroundColor: 'white',
                    borderRadius: '0.5rem',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    maxWidth: '56rem',
                    width: '100%',
                    maxHeight: '90vh',
                    overflow: 'hidden'
                }}
            >
                {/* Header */}
                <div 
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '1.5rem',
                        borderBottom: '1px solid #e5e7eb'
                    }}
                >
                    <h2 
                        style={{
                            fontSize: '1.125rem',
                            fontWeight: '600',
                            color: '#111827'
                        }}
                    >
                        {getTemplateTitle()}
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            color: '#9ca3af',
                            cursor: 'pointer',
                            border: 'none',
                            background: 'none',
                            padding: '0.25rem',
                            borderRadius: '0.25rem',
                            transition: 'color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#6b7280'}
                        onMouseLeave={(e) => e.target.style.color = '#9ca3af'}
                    >
                        <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Tabs */}
                <div style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <nav style={{ display: 'flex', gap: '2rem', padding: '0 1.5rem' }}>
                        <button
                            onClick={() => setActiveTab('preview')}
                            style={{
                                padding: '0.75rem 0.25rem',
                                borderBottom: activeTab === 'preview' ? '2px solid #3b82f6' : '2px solid transparent',
                                fontWeight: '500',
                                fontSize: '0.875rem',
                                color: activeTab === 'preview' ? '#2563eb' : '#6b7280',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                transition: 'color 0.2s, border-color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                if (activeTab !== 'preview') e.target.style.color = '#374151';
                            }}
                            onMouseLeave={(e) => {
                                if (activeTab !== 'preview') e.target.style.color = '#6b7280';
                            }}
                        >
                            Preview
                        </button>
                        <button
                            onClick={() => setActiveTab('edit')}
                            style={{
                                padding: '0.75rem 0.25rem',
                                borderBottom: activeTab === 'edit' ? '2px solid #3b82f6' : '2px solid transparent',
                                fontWeight: '500',
                                fontSize: '0.875rem',
                                color: activeTab === 'edit' ? '#2563eb' : '#6b7280',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                transition: 'color 0.2s, border-color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                if (activeTab !== 'edit') e.target.style.color = '#374151';
                            }}
                            onMouseLeave={(e) => {
                                if (activeTab !== 'edit') e.target.style.color = '#6b7280';
                            }}
                        >
                            Edit Template
                        </button>
                    </nav>
                </div>

                {/* Content */}
                <div 
                    style={{
                        padding: '1.5rem',
                        overflowY: 'auto',
                        maxHeight: '60vh'
                    }}
                >
                    {activeTab === 'preview' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <h3 style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>
                                Email Preview
                            </h3>
                            <div 
                                style={{
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '0.5rem',
                                    padding: '1rem',
                                    backgroundColor: '#f9fafb'
                                }}
                            >
                                <div 
                                    style={{
                                        backgroundColor: 'white',
                                        padding: '1rem',
                                        borderRadius: '0.25rem',
                                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                                    }}
                                    dangerouslySetInnerHTML={{ 
                                        __html: template.htmlContent || getDefaultPreview() 
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === 'edit' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            {isLoading && (
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    padding: '2rem',
                                    color: '#6b7280'
                                }}>
                                    <div style={{ marginRight: '0.5rem' }}>Loading template...</div>
                                    <div style={{
                                        width: '1rem',
                                        height: '1rem',
                                        border: '2px solid #e5e7eb',
                                        borderTop: '2px solid #3b82f6',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite'
                                    }}></div>
                                </div>
                            )}
                            {!isLoading && (
                                <>
                                {/* Subject Line */}
                                <div>
                                <label 
                                    style={{
                                        display: 'block',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}
                                >
                                    Subject Line
                                </label>
                                <input
                                    type="text"
                                    value={template.subject}
                                    onChange={(e) => setTemplate(prev => ({ ...prev, subject: e.target.value }))}
                                    placeholder="Enter email subject"
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem 0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '0.375rem',
                                        fontSize: '0.875rem',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                />
                            </div>

                            {/* Text Content */}
                            <div>
                                <label 
                                    style={{
                                        display: 'block',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}
                                >
                                    Plain Text Content (Fallback)
                                </label>
                                <textarea
                                    value={template.textContent}
                                    onChange={(e) => setTemplate(prev => ({ ...prev, textContent: e.target.value }))}
                                    placeholder="Enter plain text version"
                                    rows={8}
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem 0.75rem',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '0.375rem',
                                        fontSize: '0.875rem',
                                        transition: 'border-color 0.2s',
                                        resize: 'vertical'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                />
                                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                    Plain text version for email clients that don't support HTML
                                </p>
                            </div>

                            {/* HTML Content */}
                            <div>
                                <label 
                                    style={{
                                        display: 'block',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        color: '#374151',
                                        marginBottom: '0.5rem'
                                    }}
                                >
                                    HTML Content
                                </label>
                                <HtmlEditor
                                    value={template.htmlContent}
                                    onChange={(value) => setTemplate(prev => ({ ...prev, htmlContent: value }))}
                                    placeholder="Enter HTML email content..."
                                    height="400px"
                                    showPreview={true}
                                />
                            </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div 
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '1.5rem',
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: '#f9fafb'
                    }}
                >
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        Changes will apply to all future emails of this type
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <button
                            onClick={onClose}
                            style={{
                                padding: '0.5rem 1rem',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: '#374151',
                                backgroundColor: 'white',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.375rem',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isLoading}
                            style={{
                                padding: '0.5rem 1rem',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: 'white',
                                backgroundColor: isLoading ? '#d1d5db' : '#2563eb',
                                border: 'none',
                                borderRadius: '0.375rem',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                if (!isLoading) e.target.style.backgroundColor = '#1d4ed8';
                            }}
                            onMouseLeave={(e) => {
                                if (!isLoading) e.target.style.backgroundColor = '#2563eb';
                            }}
                        >
                            {isLoading ? 'Saving...' : 'Save Template'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default EmailTemplateModal;
